import {
  Illustration,
  Mono,
  accent,
  fail,
  ink,
  label,
  structure,
  surface,
  surfaceMuted,
  pass,
} from './primitives'

/**
 * The differentiation graphic: a single coverage line evaluated
 * deterministically. Requirement (what you configured) and Extracted value
 * (what was found on the COI) flow into a comparison node that emits an
 * explicit PASS/FAIL with reasoning — the "rules, not AI scoring" story made
 * visible. Two rows: one PASS, one FAIL, so the logic is legible at a glance.
 */
function Row({
  y,
  coverage,
  required,
  found,
  verdict,
  reason,
}: {
  y: number
  coverage: string
  required: string
  found: string
  verdict: 'PASS' | 'FAIL'
  reason: string
}) {
  const ok = verdict === 'PASS'
  const color = ok ? pass : fail
  return (
    <g>
      {/* requirement chip */}
      <rect x={20} y={y} width={150} height={44} rx={8} fill={surfaceMuted} stroke={structure} strokeWidth={1.5} />
      <Mono x={32} y={y + 17} size={9} fill={label}>REQUIREMENT</Mono>
      <text x={32} y={y + 34} fill={ink} fontSize={13} fontWeight={600} fontFamily="'Geist Variable', system-ui, sans-serif" className="tnum">{required}</text>

      {/* extracted chip */}
      <rect x={190} y={y} width={150} height={44} rx={8} fill={surface} stroke={structure} strokeWidth={1.5} />
      <Mono x={202} y={y + 17} size={9} fill={label}>FOUND ON COI</Mono>
      <text x={202} y={y + 34} fill={ink} fontSize={13} fontWeight={600} fontFamily="'Geist Variable', system-ui, sans-serif" className="tnum">{found}</text>

      {/* edges into comparator */}
      <line x1={170} y1={y + 22} x2={366} y2={y + 22} stroke={structure} strokeWidth={1.5} />
      <line x1={340} y1={y + 22} x2={366} y2={y + 22} stroke={structure} strokeWidth={1.5} />

      {/* comparator node */}
      <rect x={366} y={y + 4} width={40} height={36} rx={8} fill={ink} />
      <Mono x={386} y={y + 26} anchor="middle" size={13} fill={surface} weight={600}>=</Mono>

      {/* verdict */}
      <line x1={406} y1={y + 22} x2={432} y2={y + 22} stroke={color} strokeWidth={1.5} />
      <rect x={432} y={y + 6} width={72} height={32} rx={16} fill={ok ? '#ecfdf5' : '#fef2f2'} stroke={color} strokeWidth={1.5} />
      <circle cx={448} cy={y + 22} r={4} fill={color} />
      <Mono x={460} y={y + 26} size={11} fill={color} weight={600}>{verdict}</Mono>

      {/* reason */}
      <Mono x={520} y={y + 26} size={11} fill={label}>{reason}</Mono>

      {/* coverage label above */}
      <Mono x={20} y={y - 8} size={10} fill={ink} weight={600}>{coverage}</Mono>
    </g>
  )
}

export function DeterministicReasoning({ className }: { className?: string }) {
  return (
    <Illustration
      viewBox="0 0 900 200"
      className={className}
      title="Deterministic evaluation: requirement compared to extracted value produces an explicit PASS or FAIL with reasoning"
    >
      <Row
        y={40}
        coverage="GENERAL LIABILITY"
        required="$1M / $2M"
        found="$1M / $2M"
        verdict="PASS"
        reason="meets configured minimum"
      />
      <Row
        y={132}
        coverage="UMBRELLA / EXCESS"
        required="$2M"
        found="$1M"
        verdict="FAIL"
        reason="$1M below requirement — needs +$1M excess"
      />
      {/* header rule */}
      <line x1={20} y1={20} x2={880} y2={20} stroke={structure} strokeWidth={1} strokeDasharray="2 4" />
      <Mono x={880} y={14} anchor="end" size={9} fill={accent} weight={600}>RULES, NOT AI SCORING</Mono>
    </Illustration>
  )
}
